/**
 * Fetch Interceptor
 * Intercepts fetch responses to handle common errors like 431 and 401
 *
 * 401 handling: When a non-auth API call returns 401, it means the session
 * cookie is invalid (expired JWT, rotated secret after deploy, switching
 * environments). We dispatch an 'auth:unauthorized' event so the useAuth
 * hook can clear local state and call the server-side logout endpoint to
 * remove the HttpOnly cookie.
 */

import { logCookieDiagnostics, getCookieRecommendations, cleanupDuplicateCookies, getTotalCookieSize, deleteCookie } from './cookieManager';
import { getApiBaseUrl } from './config';

// Store original fetch so we can bypass the interceptor when needed
// (e.g., calling /auth/logout during 401 handling, or verifying session with /auth/me)
const originalFetch = window.fetch;

/** Access the original (non-intercepted) fetch for direct API calls that must bypass the 401 handler */
export { originalFetch };

let has431ErrorOccurred = false;

/**
 * Auth endpoints where 401 is expected (bad credentials, not expired session).
 * These should never trigger the auto-logout flow.
 */
const AUTH_ENDPOINT_PATTERNS = ['/auth/login', '/auth/logout', '/auth/passwordless', '/auth/refresh'];

/**
 * Non-critical endpoints where 401 should not trigger logout.
 * These endpoints may fail transiently (e.g., race conditions after login
 * where cookies haven't fully propagated yet) and their callers handle
 * errors gracefully.
 */
const SKIP_LOGOUT_PATHS = ['/users/me/mentions', '/mentions/', '/terms/'];

/**
 * Endpoints that may race with login cookie propagation.
 * Uses endsWith-style matching to avoid overly broad substring matches
 * (e.g., '/accounts' should not match '/auth/accounts').
 */
const SKIP_LOGOUT_ENDPOINT_SUFFIXES = [
  '/accounts', // Admin accounts list - prevent logout on dashboard load
  '/locations', // Locations list - prevent logout on dashboard load
];

function shouldSkipLogoutFor401(url: string): boolean {
  try {
    const urlPath = new URL(url, window.location.origin).pathname;

    // Never trigger logout for auth endpoints (login 401 = bad credentials)
    if (AUTH_ENDPOINT_PATTERNS.some((pattern) => urlPath.includes(pattern))) {
      return true;
    }

    // Skip non-critical endpoints that handle errors gracefully
    if (SKIP_LOGOUT_PATHS.some((path) => urlPath.includes(path))) {
      return true;
    }

    // Skip endpoints that may race with login (use endsWith for precision)
    if (SKIP_LOGOUT_ENDPOINT_SUFFIXES.some((suffix) => urlPath.endsWith(suffix))) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Attempt to clear the session cookie by calling the server-side logout
 * endpoint. Uses the original (non-intercepted) fetch to avoid re-triggering
 * the 401 handler. The /auth/logout endpoint does not require a valid JWT,
 * so this should succeed even with an expired/invalid cookie.
 *
 * Also attempts to delete any accessible (non-HttpOnly) cookies as a fallback.
 */
export async function clearSessionCookie(): Promise<void> {
  // Best-effort: delete any non-HttpOnly cookies we can access
  try {
    deleteCookie('turnconnect_session', '/');
    deleteCookie('turnconnect_session', '/', window.location.hostname);
    deleteCookie('turnconnect_session', '/', `.${window.location.hostname}`);
  } catch {}

  // Call server-side logout to clear the HttpOnly cookie.
  // Use originalFetch to bypass the interceptor (avoid 401 re-trigger loop).
  // The /auth/logout endpoint does not require JWT middleware, so it will
  // respond with 200 and set an expired Set-Cookie header even with an
  // invalid/expired JWT cookie.
  try {
    const apiBase = getApiBaseUrl();
    await originalFetch(`${apiBase}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
  } catch {
    // Non-critical: the auth state will be cleared regardless
  }
}

export function setupFetchInterceptor(): void {
  window.fetch = async function interceptedFetch(...args: Parameters<typeof fetch>): Promise<Response> {
    try {
      const response = await originalFetch(...args);

      // Unauthorized → broadcast once per response (skip for non-critical endpoints)
      if (response.status === 401) {
        const requestUrl = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || '';
        if (!shouldSkipLogoutFor401(requestUrl)) {
          try {
            window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { status: 401 } }));
          } catch {}
        }
      }

      // Check for 431 error
      if (response.status === 431) {
        // Only show detailed diagnostics once per session to avoid spam
        if (!has431ErrorOccurred) {
          has431ErrorOccurred = true;

          console.group('🚨 431 Request Header Fields Too Large');
          console.error('The server rejected the request because the headers are too large.');
          console.error('This is usually caused by large cookies (e.g., JWT tokens).');
          console.error('');

          // Log cookie diagnostics
          logCookieDiagnostics();
          console.error('');

          // Show recommendations
          const recommendations = getCookieRecommendations();
          if (recommendations.length > 0) {
            console.error('Recommendations:');
            recommendations.forEach((r) => console.error(r));
            console.error('');
          }

          // Provide actionable solutions
          console.error('Solutions:');
          console.error('1. Clear your browser cookies for this site');
          console.error('2. Log out and log back in');
          console.error('3. Run: cleanupDuplicateCookies() in console');
          console.error('4. Contact support if the issue persists');
          console.groupEnd();

          // Auto-attempt cleanup
          console.log('Attempting automatic cookie cleanup...');
          cleanupDuplicateCookies();

          const newSize = getTotalCookieSize();
          console.log(`Cookie size after cleanup: ${newSize} bytes (${(newSize / 1024).toFixed(2)} KB)`);

          if (newSize > 8192) {
            console.error('Cookie size still too large after cleanup. Please clear your cookies manually.');

            // Show a user-friendly alert
            if (typeof window !== 'undefined' && window.confirm) {
              const shouldClearCookies = window.confirm(
                '❌ Request failed: Headers too large\n\n' +
                  'This is usually caused by accumulated session data. ' +
                  'Would you like to log out and clear your session?\n\n' +
                  'Click OK to log out, or Cancel to continue (may cause errors).'
              );

              if (shouldClearCookies) {
                // Trigger logout
                window.location.href = '/?logout=true';
              }
            }
          } else {
            console.log('✅ Cookie cleanup successful. Please retry your request.');
          }
        }
      }

      return response;
    } catch (error) {
      // Re-throw the error for normal error handling
      throw error;
    }
  };
}

/**
 * Restore original fetch (for cleanup)
 */
export function teardownFetchInterceptor(): void {
  window.fetch = originalFetch;
  has431ErrorOccurred = false;
}
